import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { SnIntegrationsAPI } from '@lib/api/services';
import { SnCurrentOrganization } from '@lib/auth';
import { SentryVaultComponent } from '@lib/vault';

import { FsLprFleetRead, IFsLprResult, LprIntegrationType, SnLprV3FlockRead } from '../../api/';
import { FleetLprService } from '../../integrations';
import {
  PlateImageCropService,
  PlateReviewService,
  PlateImageDownloadService,
  FsLprReportGeneratorService,
} from '../../services';
import { LightboxImage } from '../image-lightbox-viewer/models/lightbox-image.model';
import { QueryResultsMapViewComponent } from '../query-results-map-view/query-results-map-view.component';

const MIN_REASON_LENGTH = 4;

@Component({
  selector: 'fs-fleet-query-plate-card-details',
  templateUrl: './fleet-query-plate-card-details.component.html',
  styleUrls: ['./fleet-query-plate-card-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FleetQueryPlateCardDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Variable to emit an event when lightbox is closed from the vehicle plate details component */
  @Output() public isLightboxClosed = new EventEmitter<FsLprFleetRead>();

  /** Event emitted when user wants to search for a specific plate */
  @Output() public searchPlate = new EventEmitter<{ plate: string; state?: string }>();

  @Input() public searchReason: string = '';

  @Input() public hideDetails: boolean = false;

  @Input() public set plate(value: FsLprFleetRead) {
    this._plate = value;
    this.isMarkedForReview = this._plateReviewService.isMarkedForReview(this._plate?.id);
    this._cd.detectChanges();
    this._maybeCropImage();
  }
  public get plate(): FsLprFleetRead {
    return this._plate;
  }

  get plateImageUrl(): string | SafeResourceUrl {
    if (this._plate?.plateImageUrl && this._plate?.plateImageKey) {
      return this._sanitizer.bypassSecurityTrustResourceUrl(this._plate.plateImageUrl);
    }
    return this._plate?.plateImageUrl;
  }

  get contextImageUrl(): string | SafeResourceUrl {
    if (this._plate?.contextImageUrl && this._plate?.overviewImageKey) {
      return this._sanitizer.bypassSecurityTrustResourceUrl(this._plate.contextImageUrl);
    }
    return this._plate?.contextImageUrl;
  }

  public isMarkedForReview: boolean = false;
  public croppedImageUrl: string | null = null;

  private _plate: FsLprFleetRead | null = null;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _imageCropService: PlateImageCropService,
    private readonly _plateReviewService: PlateReviewService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _integrationsAPI: SnIntegrationsAPI,
    private readonly _lprFleetService: FleetLprService,
    private readonly _plateImageDownloadService: PlateImageDownloadService,
    private readonly _matDialog: MatDialog,
    private readonly _lprReportGeneratorService: FsLprReportGeneratorService,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _sanitizer: DomSanitizer,
  ) {}

  get lprSharingEnabled(): boolean {
    return this._currentOrganization.settings?.lprSharingManagementEnabled ?? false;
  }

  get hasExternalFlockV3Link(): boolean {
    return (
      this._plate?.integratedLprType === LprIntegrationType.FLOCK_V3 &&
      !!(this._plate as IFsLprResult as SnLprV3FlockRead).imageUrl
    );
  }

  get shouldShowDownloadButton(): boolean {
    return !this.hasExternalFlockV3Link;
  }

  ngOnInit(): void {
    this._plateReviewService.getReviewedPlates().subscribe((plates) => {
      this.isMarkedForReview = plates.some((plate) => plate.id === this.plate?.id);
      this._cd.detectChanges();
    });
  }

  public ngAfterViewInit(): void {
    this._maybeCropImage();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Emit an event when lightbox is closed from the vehicle plate details component
   */
  public onLightBoxClosed(): void {
    this.isLightboxClosed.emit(this.plate);
  }

  public toggleMarkForReview(): void {
    this.isMarkedForReview = this._plateReviewService.isMarkedForReview(this.plate?.id);
    if (this.isMarkedForReview) {
      this._plateReviewService.removeFromReview(this.plate?.id);
    } else {
      this._plateReviewService.markForReview(this.plate);
    }
    this._cd.detectChanges();
  }

  public get lightboxImages(): LightboxImage[] {
    if (!this.plate?.contextImageUrl) {
      return [];
    }

    const image: LightboxImage = {
      url: this.contextImageUrl as string,
    };
    const images = [image];

    if (this.croppedImageUrl) {
      const croppedImage: LightboxImage = {
        url: this.croppedImageUrl,
        class: 'cropped-image',
      };
      images.unshift(croppedImage);
    }

    return images;
  }

  /**
   * Search for the exact plate across all the providers.
   */
  public isSearchReasonValid(): boolean {
    return this.searchReason?.length >= MIN_REASON_LENGTH;
  }

  public onSearchPlate(): void {
    if (this.plate && this.isSearchReasonValid()) {
      // Emit an event with the plate number and state to be handled by the parent component
      this.searchPlate.emit({
        plate: this.plate.plate,
        ...(this.plate.displayState && { state: this.plate.state }),
      });
    }
  }

  public downloadImage(plate: FsLprFleetRead): void {
    this._plateImageDownloadService.downloadImage(plate, this.croppedImageUrl);
  }

  public downloadPlate(plate: FsLprFleetRead): void {
    this._plateImageDownloadService.downloadPlate(plate);
  }

  public showOnMap(value: any): void {
    const mapResults = this._matDialog.open(QueryResultsMapViewComponent, {
      data: value,
      width: '75vw',
      hasBackdrop: true,
    });
    mapResults
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {});
  }

  public sendToVault(plate: FsLprFleetRead): void {
    this._lprReportGeneratorService.generatePdfReport(plate).then((blob) => {
      const now = new Date();
      const file = new File([blob], `ALPR_Report_${plate.plate}_${now.getTime()}.pdf`, {
        type: blob.type,
      });
      const formData = new FormData();
      formData.append('files', file, file.name);
      const dialog = this._matDialog.open(SentryVaultComponent);
      dialog
        .afterClosed()
        .pipe(takeUntil(this._destroy$))
        .subscribe((vaultID) => {
          const vault = vaultID?.id || vaultID;
          if (vault) {
            this._addToVault(vault, formData);
          }
        });
    });
  }

  private _addToVault(vault: string, formData: any): void {
    this._lprFleetService.addMediaToVault(vault, formData).subscribe();
  }

  private _maybeCropImage(): void {
    if (this.plate?.contextImageUrl && !this.plate.croppedContextImageUrl) {
      const canvas = document.createElement('canvas');

      this._imageCropService
        .cropImage(
          this.plate,
          canvas,
          PlateImageCropService.DETAILS_DIMENSIONS,
          true, // Show bounding box in details view
        )
        .then((croppedUrl) => {
          this.croppedImageUrl = croppedUrl;
          this._cd.detectChanges();
        })
        .catch((err) => {
          this.croppedImageUrl = null;
          this._cd.detectChanges();
        });
    }
  }

  /**
   * Creates an array of characters from the plate number.
   * If the plate is undefined, returns a default array with specified length.
   * @returns An array of characters representing the plate.
   */
  public getPaddedPlateChars(): string[] {
    if (!this.plate?.plate) {
      return Array(9).fill('');
    }

    return this.plate.plate.toString().trim().toUpperCase().split('');
  }
}
